import { inject, Injectable } from '@angular/core';
import { Observable, of, catchError, from, throwError } from 'rxjs';
import {
  UsersApi,
  UserList,
  GetUsersStatusEnum,
  GetUsersRequest,
  PatchUser,
  CreateUser,
  User,
  UpdateUserSkills,
  UpdateUserTags,
} from '@tourfold/libraries/tf-api-client-public-v2';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly usersApi = inject(UsersApi);

  getUsersData(data: GetUsersRequest): Observable<UserList> {
    return from(
      this.usersApi.getUsers({
        page: data.page ?? 0,
        size: data.size ?? 50,
        sort: data.sort,
        search: data.search,
        role: data.role,
        status: (data.status as GetUsersStatusEnum) ?? GetUsersStatusEnum.Active,
        roleIds: data.roleIds,
        skillIds: data.skillIds,
        tagNames: data.tagNames,
      })
    ).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  editUser(userId: string, patchUser: PatchUser): Observable<User> {
    return from(
      this.usersApi.patchUser({
        userId,
        patchUser,
      })
    ).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  createUser(createUser: CreateUser): Observable<User> {
    return from(
      this.usersApi.createUser({
        createUser,
      })
    ).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  updateUserSkills(userId: string, updateUserSkills: UpdateUserSkills): Observable<User> {
    return from(
      this.usersApi.updateUserSkills({
        userId,
        updateUserSkills,
      })
    ).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  updateUserTags(userId: string, updateUserTags: UpdateUserTags): Observable<User> {
    return from(
      this.usersApi.updateUserTags({
        userId,
        updateUserTags,
      })
    ).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  deleteUser(userId: string): Observable<void> {
    return from(
      this.usersApi.deleteUser({
        userId,
      })
    ).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  getCurrentUser(): Observable<User> {
    return from(this.usersApi.getCurrentUser()).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  activateUser(userId: string): Observable<User> {
    return from(
      this.usersApi.activateUser({
        userId,
      })
    ).pipe(
      catchError((error) => {
        if (error?.status === 204 || error?.message?.includes('Unexpected end of JSON')) {
          return of({} as User);
        }
        return throwError(() => error);
      })
    );
  }

  deactivateUser(userId: string): Observable<User> {
    return from(
      this.usersApi.deactivateUser({
        userId,
      })
    ).pipe(
      catchError((error) => {
        if (error?.raw?.status === 204) {
          return of({} as User);
        }
        return throwError(() => error);
      })
    );
  }

  resendSetPasswordEmail(userId: string): Observable<void> {
    return from(
      this.usersApi.resendSetPasswordEmail({
        userId,
      })
    ).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  unblockUser(userId: string): Observable<void> {
    return from(
      this.usersApi.unblockUser({
        userId,
      })
    ).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  makeUserOwner(userId: string): Observable<User> {
    return from(
      this.usersApi.makeUserOwner({
        userId,
      })
    ).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }
}
